import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyTeamSettings({ user }) {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [showIndividualStats, setShowIndividualStats] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    if (isAdmin) {
      fetchTeamSettings();
    }
  }, [teamId]);

  const fetchTeamSettings = async () => {
    try {
      const response = await axios.get(`${API}/academy/teams/${teamId}`, {
        withCredentials: true
      });
      setTeam(response.data);
      setShowStats(response.data.show_stats !== false);
      setShowIndividualStats(response.data.show_individual_stats || false);
    } catch (error) {
      console.error("Error fetching team:", error);
      toast.error("Failed to load team settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/academy/teams/${teamId}/settings`,
        {
          show_stats: showStats,
          show_individual_stats: showIndividualStats
        },
        { withCredentials: true }
      );

      toast.success("Team settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8">
          <p className="text-slate-400">Access denied. Admin only.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <Link to={`/academy/teams/${teamId}`}>
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Team
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <h2 className="text-3xl font-bold text-white mb-2">{team?.name} - Settings</h2>
        <p className="text-slate-400 mb-8">Configure what parents see for this team</p>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-white mb-6">Parent View Settings</h3>

          <div className="mb-6 p-4 border border-slate-700 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-semibold text-white mb-1">Show Team Performance Stats</h4>
                <p className="text-sm text-slate-400">
                  Display games played, won, lost, draw to parents
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={showStats}
                  onChange={(e) => setShowStats(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-900/50 after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          <div className="mb-6 p-4 border border-slate-700 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-semibold text-white mb-1">Show Individual Player Stats</h4>
                <p className="text-sm text-slate-400">
                  Display goals, assists, MOTM to parents
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Consider turning this OFF for younger age groups to avoid wrong signals
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={showIndividualStats}
                  onChange={(e) => setShowIndividualStats(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-900/50 after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm font-semibold text-blue-300 mb-2">Preview (Parent View):</p>
            {showStats ? (
              <div>
                <p className="text-xs text-slate-300 mb-2">✅ Team performance: Played, Won, Lost, Draw</p>
                {showIndividualStats && (
                  <p className="text-xs text-slate-300">✅ Individual stats: Goals, Assists, MOTM</p>
                )}
                {!showIndividualStats && (
                  <p className="text-xs text-slate-400">❌ Individual stats hidden</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-400">❌ All stats hidden</p>
            )}
          </div>

          <Button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? (
              <>
                <div className="spinner mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </Card>

        <Card className="p-4 mt-6 bg-yellow-500/10 border border-white/5">
          <p className="text-sm text-yellow-400">
            <strong>💡 Tip:</strong> For younger age groups (U8, U9), consider hiding individual stats to focus on team play and enjoyment rather than competition.
          </p>
        </Card>
      </div>
    </div>
  );
}
